import { FileActionButton } from "../file-action/file-action-controls";

import { useEffect, useState } from "react";
import oilProductionService from "../../services/oilProduction/oilProductionservice";
import { SeedOilData } from "../../models/oilProduction-model";
import { Calendar } from "primereact/calendar";
import SeedOilDataTable from "./SeedOilDataTable";
import { Card } from "primereact/card";
import { BreadCrumb } from "primereact/breadcrumb";
import * as XLSX from "xlsx";
import { seedOilExport } from "./monthlyAnalysis-Export";
import RawMaterialService from "../../services/RawMaterials/RawMaterialService";
import { MultiSelect } from "primereact/multiselect";

const SeedOilComponent = () => {
  const getCurrentMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, "0");
    return `${year}-${month}`;
  };

  const [seedOilData, setSeedOilData] = useState<SeedOilData[]>([]);
  const [month, setMonth] = useState(getCurrentMonth());
  const [loading, setLoading] = useState(false);
  const [totalOil, setTotalOil] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  const [noProdBatches, setNoProdBatches] = useState(0);
  const [constantProductionCount, setConstantProductionCount] = useState<number>(0);
  const [manufacturingUnits, setManufacturingUnits] = useState<any[]>([]);
  const [selectedUnits, setSelectedUnits] = useState<number[]>([]);

  // Helper: alias MU name for display
  const aliasMU = (id: number, addr?: string, alias?: string) => {
    const code = (alias ?? "").toUpperCase().trim();
    const up = (addr ?? "").toUpperCase().replace(/\s+/g, " ");
    if (id === 3 || code === "KRN1" || /\bK\s*R\s*NAGAR\b/.test(up) || up.includes("KR NAGAR")) return "Unit-1-KR";
    if (id === 4 || code === "MGT" || up.includes("MAGADI TALUK") || up.includes("MAGADI")) return "Unit-2-MT";
    return addr ?? "";
  };

  useEffect(() => {
    const fetchManufacturingUnits = async () => {
      try {
        const list = await RawMaterialService.GetManufacrturingunitlist();
        if (list) {
          // ONLY CHANGE: alias display label and normalize id
          const formattedList = (list ?? []).map((unit: any) => {
            const id = Number(unit?.posID ?? unit?.PosID ?? unit?.id);
            const addr2 = unit?.address2 ?? unit?.Address2 ?? unit?.posName ?? unit?.PosName ?? "";
            const posAlias = unit?.POSAlias ?? unit?.posAlias ?? "";
            return {
              label: aliasMU(id, addr2, posAlias), // show Unit-1-KR / Unit-2-MT
              value: id,
            };
          });

          setManufacturingUnits(formattedList);
          const allIds: number[] = formattedList.map((unit: any) => unit.value);
          setSelectedUnits(allIds);
        }
      } catch (error) {
        console.error("Failed to fetch manufacturing units", error);
      }
    };

    fetchManufacturingUnits();
  }, []);

  useEffect(() => {
    if (!month) return;

    const loadSeedOilData = async (monthParam: string, posIDs: number[] = []) => {
      try {
        setLoading(true);
        const response: SeedOilData[] = await oilProductionService.getSeedOilByMonth(monthParam, posIDs);

        setSeedOilData(response ?? []);

        let totalOilValue = 0;
        let totalBatchesValue = 0;
        let noProdValue = 0;
        let availableCapacityValue = 0;

        response.forEach((item: SeedOilData) => {
          if (item.seedName === "Total") {
            totalOilValue = item.oilInLtr;
            totalBatchesValue = item.noOfBatches;
          }
          if (item.seedName === "No Prod") {
            noProdValue = item.noOfBatches;
          }
          if (item.seedName === "Available Capacity") {
            availableCapacityValue = item.noOfBatches;
          }
        });

        setTotalOil(totalOilValue);
        setTotalBatches(totalBatchesValue);
        setNoProdBatches(noProdValue);
        setConstantProductionCount(availableCapacityValue);
      } catch (error) {
        console.error("Error loading seed oil data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadSeedOilData(month, selectedUnits);
  }, [month, selectedUnits]);

  const breadcrumbItems = [
    { label: "Oil Production", url: "" },
    { label: "Oil Production Summary Analysis", url: "" },
  ];
  const home = { icon: "pi pi-home", url: "/" };

  return (
    <div className="layout-content mt-3 mb-3 grid grid-table-mobile">
      <div className="col-12">
        <div className="flex align-items-center justify-content-between flex-wrap gap-2 w-full card-container mb-0">
          <h5 className=" mb-0">Oil Production Summary Analysis</h5>
          <FileActionButton action="export"
            className="text-black border-round mb-0"
            icon="pi pi-file-export"
            text
            raised
            tooltip="Export to Excel"
            tooltipOptions={{ position: "top" }}
            style={{ width: "auto" }}
            disabled={seedOilData.length === 0}
            onClick={() => seedOilExport(seedOilData)}
          >
            Export Monthly Analysis Report
          </FileActionButton>
        </div>
        {/* <BreadCrumb
          model={breadcrumbItems}
          home={home}
          className="bg-transparent px-0 py-0 text-sm text-gray-600 mb-2"
          style={{
            border: "none",
            boxShadow: "none",
            background: "transparent",
            marginLeft: "5px",
          }}
        /> */}
      </div>

      <div className="col-12 card border px-3 pt-3 pb-3 mb-0">
        <div className="flex justify-center align-items-center flex-wrap gap-3 w-full">
          <Calendar
            value={month ? new Date(month + "-01") : null}
            onChange={(e) => {
              if (e.value) {
                const selectedDate = e.value as Date;
                const year = selectedDate.getFullYear();
                const monthValue = (selectedDate.getMonth() + 1).toString().padStart(2, "0");
                const formattedMonth = `${year}-${monthValue}`;
                setMonth(formattedMonth);
              }
            }}
            view="month"
            dateFormat="M, yy"
            maxDate={new Date()}
            showIcon
            className="custom-calendar"
          />
          <MultiSelect
            value={selectedUnits}
            options={manufacturingUnits}
            onChange={(e) => setSelectedUnits(e.value)}
            placeholder="Select Manufacturing Units"
            display="chip"
            className="w-full sm:w-16rem"
            filter
          />
        </div>
      </div>

      <div className="col-12" aria-hidden="true" style={{ display: "flex", gap: "0rem" }}>
        <div className="col-3">
          <Card title="Total Oil Produced" style={{ borderTop: "4px solid #0d6efd" }}>
            <span style={{ fontWeight: "bold", fontSize: "1.5rem", color: "#212529" }}>
              {totalOil.toLocaleString("en-IN", { minimumFractionDigits: 2 })} Ltr
            </span>
          </Card>
        </div>

        <div className="col-3">
          <Card title="Available Capacity" style={{ borderTop: "4px solid #198754" }}>
            <span style={{ fontWeight: "bold", fontSize: "1.5rem", color: "#212529" }}>
              {constantProductionCount}
              <span
                style={{
                  fontWeight: "normal",
                  fontSize: "1rem",
                  marginLeft: "8px",
                  color: "#6c757d",
                }}
              >
                {constantProductionCount > 0 ? (constantProductionCount / constantProductionCount) * 100 : 0}%
              </span>
            </span>
          </Card>
        </div>

        <div className="col-3">
          <Card title="Utilized Capacity " style={{ borderTop: "4px solid #fd7e14" }}>
            <span style={{ fontWeight: "bold", fontSize: "1.5rem", color: "#212529" }}>
              {totalBatches}
              <span
                style={{
                  fontWeight: "normal",
                  fontSize: "1rem",
                  marginLeft: "8px",
                  color: "#6c757d",
                }}
              >
                (
                {constantProductionCount > 0 ? ((totalBatches / constantProductionCount) * 100).toFixed(1) : 0}
                %)
              </span>
            </span>
          </Card>
        </div>

        <div className="col-3">
          <Card title="Capacity Loss" style={{ borderTop: "4px solid #dc3545" }}>
            <span style={{ fontWeight: "bold", fontSize: "1.5rem", color: "#212529" }}>
              {noProdBatches}
              <span
                style={{
                  fontWeight: "normal",
                  fontSize: "1rem",
                  marginLeft: "8px",
                  color: "#6c757d",
                }}
              >
                (
                {constantProductionCount > 0 ? ((noProdBatches / constantProductionCount) * 100).toFixed(1) : 0}
                %)
              </span>
            </span>
          </Card>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="w-12 h-12 border-4 border-blue-300 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <SeedOilDataTable seedOilData={seedOilData} />
      )}
    </div>
  );
};

export default SeedOilComponent;
